import React from "react";
import { HiddenVisually } from "web/react/emo/hidden-visually";
import { BaseText } from "web/react/emo/shared/components/base-text/base-text";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./price.css";

const FontsMap = {
    xs: "body-s-bold",
    sm: "body-bold",
    md: "subhead",
    xm: "title-3",
    lg: "title-2",
} as const;

export interface PriceProps {
    fullPrice: string | number;
    inStock: boolean;
    dataTestId?: string;
    salePrice?: string | number | null;
    size?: keyof typeof FontsMap;
}

export function Price({
    fullPrice,
    salePrice,
    inStock,
    dataTestId,
    size = "sm",
}: PriceProps): JSX.Element {
    const textStyle = FontsMap[size];

    let prices: React.ReactNode;
    const hideOriginalPrice = !inStock && salePrice;

    if (hideOriginalPrice) {
        // The original price is omitted, so there is no comparison to announce.
        prices = (
            <BaseText textStyle={textStyle} strikeThrough color="highlightBrand">
                {salePrice}
            </BaseText>
        );
    } else if (salePrice) {
        prices = (
            <>
                <BaseText as="del" textStyle={textStyle} strikeThrough>
                    <HiddenVisually>
                        {gettext("product.price.was", { price: fullPrice })}
                    </HiddenVisually>
                    <span aria-hidden="true">{fullPrice}</span>
                </BaseText>
                <BaseText textStyle={textStyle} color="highlightBrand">
                    <HiddenVisually>
                        {gettext("product.price.now", { price: salePrice })}
                    </HiddenVisually>
                    <span aria-hidden="true">{salePrice}</span>
                </BaseText>
            </>
        );
    } else {
        prices = (
            <BaseText textStyle={textStyle} strikeThrough={!inStock}>
                {fullPrice}
            </BaseText>
        );
    }

    return (
        <div data-testid={dataTestId || "product-price"} className={styles.priceContainer}>
            {prices}
        </div>
    );
}
